import { memo, useCallback } from 'react';

import { Drawer } from 'components/drawer';
import { formatPrice } from 'utils/helpers';
import { isMobile, useNavigate } from 'hooks';
import { formatCalendarDate } from 'utils/date';
import { EventProfitAndLossLtlDetail } from 'store/api/event/types';

import * as LineStyles from '../line-check/styles';
import { RecordTable, RecordColumn } from '../record-table';
import {
  LineDifference,
  useFitDrawerTop,
  LineDrawerHeader,
  useEscapeToClose,
} from '../line-check';

import { LtlDrawerProps } from './types';
import {
  titles,
  getLtlTotal,
  ltlSourceLabels,
  getLtlDetailsRoute,
} from './config';

const [dateTitle, typeTitle, carrierTitle, invoiceTitle, amountTitle] = titles;

// Every column the drawer had; Carrier (the vendor) is the flexible column.
const columns: RecordColumn<EventProfitAndLossLtlDetail>[] = [
  {
    key: 'date',
    kind: 'date',
    title: dateTitle,
    value: (row) => formatCalendarDate(row.date),
  },
  {
    kind: 'flex',
    key: 'carrier',
    title: carrierTitle,
    value: (row) => row.carrierName,
  },
  {
    key: 'type',
    kind: 'nowrap',
    title: typeTitle,
    value: (row) => ltlSourceLabels[row.source] ?? row.source,
  },
  {
    key: 'invoice',
    kind: 'nowrap',
    title: invoiceTitle,
    value: (row) => row.invoiceNumber,
  },
  {
    key: 'amount',
    kind: 'amount',
    title: amountTitle,
    value: (row) => formatPrice(row.amount),
  },
];

export const LtlDrawer = memo(
  ({ opened, onClose, eventName, lineAmount, ltlDetails }: LtlDrawerProps) => {
    const mobile = isMobile();
    const navigate = useNavigate();
    const { ref, topOffset } = useFitDrawerTop(mobile);
    useEscapeToClose(!!opened && !!ltlDetails?.length, onClose);

    const handleSelect = useCallback(
      (value: EventProfitAndLossLtlDetail) =>
        navigate(getLtlDetailsRoute(value)),
      [navigate]
    );

    if (!opened || !ltlDetails?.length) {
      return null;
    }

    return (
      <Drawer elevated hideOverlay opened={true} topOffset={topOffset}>
        <LineStyles.DrawerContent ref={ref}>
          <LineDrawerHeader
            title="LTL"
            onClose={onClose}
            subtitle={eventName}
          />
          <RecordTable
            columns={columns}
            rows={ltlDetails}
            totalLabel="Total LTL"
            onRowClick={handleSelect}
            total={getLtlTotal(ltlDetails)}
            getKey={(row) => `${row.source}-${row.id}`}
            getRowLabel={(row) =>
              `Open ${ltlSourceLabels[row.source] ?? row.source} ${formatPrice(row.amount)}`
            }
          />
          <LineDifference
            lineAmount={lineAmount}
            total={getLtlTotal(ltlDetails)}
          />
        </LineStyles.DrawerContent>
      </Drawer>
    );
  }
);
